/**
 * Copyright © 2012 Goodgame Studios. All rights reserved.
 *
 * Created with IntelliJ IDEA
 * Creator: Patrick
 * Created: 30.07.12, 11:58
 *
 * $URL$
 * $Rev$
 * $Author$
 * $Date$
 * $Id$
 */

/**
 * Source, see:
 * http://www.computus.org/journal/?p=44
 */
export class TimezoneUtil {
    /**
     * Timezone is calculated assuming a UTC range from UTC-12 till UTC+12
     *
     * The final timezone will be recalculated to a positive int from 1 - 25.
     *
     * All possible values:
     * UTC: -12, flash timezoneOffset: 720 -> tracking output: 1
     * UTC: -11, flash timezoneOffset: 660 -> tracking output: 2
     * UTC: -10, flash timezoneOffset: 600 -> tracking output: 3
     * UTC: -9, flash timezoneOffset: 540 -> tracking output: 4
     * UTC: -8, flash timezoneOffset: 480 -> tracking output: 5
     * UTC: -7, flash timezoneOffset: 420 -> tracking output: 6
     * UTC: -6, flash timezoneOffset: 360 -> tracking output: 7
     * UTC: -5, flash timezoneOffset: 300 -> tracking output: 8
     * UTC: -4, flash timezoneOffset: 240 -> tracking output: 9
     * UTC: -3, flash timezoneOffset: 180 -> tracking output: 10
     * UTC: -2, flash timezoneOffset: 120 -> tracking output: 11
     * UTC: -1, flash timezoneOffset: 60 -> tracking output: 12
     * UTC: 0, flash timezoneOffset: 0 -> tracking output: 13
     * UTC: 1, flash timezoneOffset: -60 -> tracking output: 14
     * UTC: 2, flash timezoneOffset: -120 -> tracking output: 15
     * UTC: 3, flash timezoneOffset: -180 -> tracking output: 16
     * UTC: 4, flash timezoneOffset: -240 -> tracking output: 17
     * UTC: 5, flash timezoneOffset: -300 -> tracking output: 18
     * UTC: 6, flash timezoneOffset: -360 -> tracking output: 19
     * UTC: 7, flash timezoneOffset: -420 -> tracking output: 20
     * UTC: 8, flash timezoneOffset: -480 -> tracking output: 21
     * UTC: 9, flash timezoneOffset: -540 -> tracking output: 22
     * UTC: 10, flash timezoneOffset: -600 -> tracking output: 23
     * UTC: 11, flash timezoneOffset: -660 -> tracking output: 24
     * UTC: 12, flash timezoneOffset: -720 -> tracking output: 25
     *
     * @return ggs timezone
     */
    public static getTrackingTimezone(): number {
        return (Math.round(TimezoneUtil.getFlashTimezoneWithoutDST() * -1 / 60) + 13);
    }

    /**
     * Timezone is calculated assuming a UTC range from UTC-12 till UTC+12 and multiplying by 10
     * so we get values between -120,+120
     */
    public static getTrackingTimezoneInUTC(): number {
        return TimezoneUtil.convertFlashTimezoneToUTCTimezone(TimezoneUtil.getFlashTimezoneWithoutDST()) * 10;
    }

    /**
     * Calculates the UTC timezone offset (range from UTC-12 till UTC+12, value in hours)
     * without the DST (daylight savings time).
     *
     * @return UTC timezone
     */
    public static getUTCTimezoneWithoutDST(): number {
        return TimezoneUtil.convertFlashTimezoneToUTCTimezone(TimezoneUtil.getFlashTimezoneWithoutDST());
    }

    /**
     * Calculates the UTC timezone offset (range from UTC-12 till UTC+12, value in hours)
     * with the current DST (daylight savings time) applied, if there is any.
     *
     * @return UTC timezone
     */
    public static getUTCTimezoneWithDST(): number {
        return TimezoneUtil.convertFlashTimezoneToUTCTimezone(TimezoneUtil.getFlashTimezoneWithDST());
    }

    /**
     * Calculates the flash timezone offset of the current location (range from -720 till 720, value in minutes)
     * without the DST (daylight savings time) applied (if there even is any).
     *
     * @return flash timezone
     */
    public static getFlashTimezoneWithoutDST(): number {
        // let the fiendish trickery commence!
        let currentYear: number = (new Date()).getFullYear();

        // create two dates: one in summer and one in winter
        // (though we don't know which is which because there is a southern hemisphere, remember?) 
        let date1: Date = new Date(currentYear, 0, 1); // 1st of january, this year
        let date2: Date = new Date(currentYear, 6, 1); // 1st of july, this year

        // The larger of the two dates' timezoneOffsets is considered 'winter time' (== normal time).
        let timezoneOffsetWithoutDST: number = Math.max(date1.getTimezoneOffset(), date2.getTimezoneOffset());

        // Don't get confused: to save daylight in the winter, you need to adjust the clocks *backwards* - which would
        // result in a *smaller* normal time, but since the Flash timezoneOffset has an inverted sign compared to UTC,
        // it's actually the larger value. Everything's fine!

        return timezoneOffsetWithoutDST;
    }

    /**
     * Calculates the flash timezone offset of the current location (range from -720 till 720, value in minutes)
     * with the current DST (daylight savings time) applied (if there even is any).
     *
     * @return flash timezone
     */
    public static getFlashTimezoneWithDST(): number {
        let date: Date = new Date();
        return date.getTimezoneOffset();
    }

    /**
     * Calculates the current DST (daylight savings time) in hours
     *
     * @return daylight savings time (DST)
     */
    public static getDST(): number {
        let timezoneWithoutDST: number = TimezoneUtil.getFlashTimezoneWithoutDST();
        let dst: number = (new Date().getTimezoneOffset()) - timezoneWithoutDST;

        // convert to UTC offset in hours
        return TimezoneUtil.convertFlashTimezoneToUTCTimezone(dst);
    }

    /**
     * Indicates if the current timezone/location does *generally* have a DST (daylight savings time).
     *
     * You can check if the DST is active *right now* with 'isDSTActive()'.
     *
     * @return whether current location has a DST
     */
    public static hasDST(): boolean {
        // let the fiendish trickery commence!
        let currentYear: number = (new Date()).getFullYear();

        // create two dates: one in summer and one in winter
        let date1: Date = new Date(currentYear, 0, 1); // 1st of january, this year
        let date2: Date = new Date(currentYear, 6, 1); // 1st of july, this year

        // if the timezone offsets of the two dates are not the same, there seems to be a DST!
        return (date1.getTimezoneOffset() !== date2.getTimezoneOffset());
    }

    /**
     * Indicates if the current timezone/location has a DST (daylight savings time) active *right now*.
     *
     * You can check if there *generally* is a DST at this location with 'hasDST()'.
     *
     * @return daylight savings time (DST)
     */
    public static isDSTActive(): boolean {
        let timezoneWithoutDST: number = TimezoneUtil.getFlashTimezoneWithoutDST();
        let dst: number = (new Date()).getTimezoneOffset() - timezoneWithoutDST;
        return (dst === 0) ? false : true;
    }

    /**
     * Converts a given Flash timezone to its UTC equivalent.
     * UTC timezones range reaches from -12 to +12, the values representing hours.
     * Flash timezone offset reaches from -720 to +720, the values representing minutes *and* being inverted,
     * since its definition in the official api documentation for the Date.timezoneOffset property:
     * "The difference, in minutes, between universal time (UTC) and the computer's local time."
     * E.g. USA:
     * Flashplayer: 360
     * UTC timezone: -6
     *
     * @param flashTimezone
     * @return UTC timezone
     */
    public static convertFlashTimezoneToUTCTimezone(flashTimezone: number): number {
        // convert to hours
        return (flashTimezone / 60) * -1;
    }

    /**
     * Converts a given UTC timezone to its flash equivalent.
     * UTC timezones range reaches from -12 to +12, the values representing hours.
     * Flash timezone offset reaches from -720 to +720, the values representing minutes *and* being inverted,
     * since its definition in the official api documentation for the Date.timezoneOffset property:
     * "The difference, in minutes, between universal time (UTC) and the computer's local time."
     * E.g. USA:
     * UTC timezone: -6
     * Flashplayer: 360
     *
     * @param utcTimezone
     * @return Flashplayer timezone
     */
    public static convertUTCTimezoneToFlashTimezone(utcTimezone: number): number {
        return (utcTimezone * 60) * -1;
    }
}
